/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import { useCallback, useEffect, useRef } from 'react';

import useScript from './useScript';

type ErrorCode =
  | 'invalid_request'
  | 'access_denied'
  | 'unauthorized_client'
  | 'unsupported_response_type'
  | 'invalid_scope'
  | 'server_error'
  | 'temporarily_unavailable';

interface TokenResponse {
  access_token: string;
  error?: ErrorCode;
  error_description?: string;
  error_uri?: string;
  expires_in: number;
  hd?: string;
  prompt: string;
  scope: string;
  state?: string;
  token_type: string;
}

type NonOAuthError = {
  type: 'popup_failed_to_open' | 'popup_closed' | 'unknown';
};
interface TokenClientConfig {
  callback?: (response: TokenResponse) => void;
  client_id: string;
  enable_serial_consent?: boolean;
  error_callback?: (nonOAuthError: NonOAuthError) => void;
  hint?: string;
  hosted_domain?: string;
  include_granted_scopes?: boolean;
  prompt?: '' | 'none' | 'consent' | 'select_account';
  scope: string;
  state?: string;
}

interface OverridableTokenClientConfig {
  enable_serial_consent?: boolean;
  hint?: string;
  prompt?: '' | 'none' | 'consent' | 'select_account';
  state?: string;
}

interface CodeResponse {
  code: string;
  error?: ErrorCode;
  error_description?: string;
  error_uri?: string;
  scope: string;
  state?: string;
}

interface CodeClientConfig {
  callback?: (codeResponse: CodeResponse) => void;
  client_id: string;
  enable_serial_consent?: boolean;
  hint?: string;
  hosted_domain?: string;
  include_granted_scopes?: boolean;
  redirect_uri?: string;
  scope: string;
  select_account?: boolean;
  state?: string;
  ux_mode?: 'popup' | 'redirect';
}

interface ImplicitFlowOptions extends Omit<TokenClientConfig, 'client_id' | 'scope' | 'callback'> {
  onError?: (
    errorResponse: Pick<TokenResponse, 'error' | 'error_description' | 'error_uri'>
  ) => void;
  onNonOAuthError?: (nonOAuthError: NonOAuthError) => void;
  onSuccess?: (
    tokenResponse: Omit<TokenResponse, 'error' | 'error_description' | 'error_uri'>
  ) => void;
  overrideScope?: boolean;
  scope?: TokenClientConfig['scope'];
}

interface AuthCodeFlowOptions extends Omit<CodeClientConfig, 'client_id' | 'scope' | 'callback'> {
  onError?: (
    errorResponse: Pick<CodeResponse, 'error' | 'error_description' | 'error_uri'>
  ) => void;
  onNonOAuthError?: (nonOAuthError: NonOAuthError) => void;
  onSuccess?: (
    codeResponse: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>
  ) => void;
  overrideScope?: boolean;
  scope?: CodeResponse['scope'];
}

type UseGoogleLoginOptionsImplicitFlow = {
  flow?: 'implicit';
} & ImplicitFlowOptions;

type UseGoogleLoginOptionsAuthCodeFlow = {
  flow?: 'auth-code';
} & AuthCodeFlowOptions;

type UseGoogleLoginOptions = UseGoogleLoginOptionsImplicitFlow | UseGoogleLoginOptionsAuthCodeFlow;

export default function useGoogleLogin({
  flow = 'implicit',
  scope = '',
  onSuccess,
  onError,
  onNonOAuthError,
  overrideScope,
  state,
  ...props
}: UseGoogleLoginOptions) {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const [loading] = useScript({
    src: 'https://accounts.google.com/gsi/client'
  });

  const clientRef = useRef<any>(undefined);

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const onNonOAuthErrorRef = useRef(onNonOAuthError);
  onNonOAuthErrorRef.current = onNonOAuthError;

  useEffect(() => {
    if (loading) return;

    const clientMethod = flow === 'implicit' ? 'initTokenClient' : 'initCodeClient';

    // @ts-ignore
    clientRef.current = window?.google?.accounts.oauth2[clientMethod]({
      callback: (response: TokenResponse | CodeResponse) => {
        if (response.error) return onErrorRef.current?.(response);

        onSuccessRef.current?.(response as any);
      },
      client_id: clientId,
      error_callback: (nonOAuthError: NonOAuthError) => {
        onNonOAuthErrorRef.current?.(nonOAuthError);
      },
      scope: overrideScope ? scope : `openid profile email ${scope}`,
      state,
      ...props
    });
  }, [clientId, loading, flow, scope, state]);

  const loginImplicitFlow = useCallback(
    (overrideConfig?: OverridableTokenClientConfig) =>
      clientRef.current?.requestAccessToken(overrideConfig),
    []
  );

  const loginAuthCodeFlow = useCallback(() => clientRef.current?.requestCode(), []);

  return flow === 'implicit' ? loginImplicitFlow : loginAuthCodeFlow;
}
